/**
 * AciColorPicker — shared ACI color selection popover (Faz 5.2).
 * 256-index classic palette (standard strip + 24×10 hue grid + grayscale),
 * ByLayer/ByBlock rows, a true-color hex input and the recent-colors strip
 * persisted in localStorage. Portal-based (Chakra Popover) so it floats over
 * panel/tab chrome; consumes semantic tokens for its own chrome while the
 * swatches themselves carry palette (data) colors.
 */

import {
    Box,
    Flex,
    Grid,
    HStack,
    Input,
    Popover,
    PopoverAnchor,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ACI_GRID, aciName, aciToHex, hexToAci,
    COLOR_BYLAYER, COLOR_BYBLOCK,
} from './aciColor';

const RECENT_KEY = 'yap-aci-recent';
const RECENT_CAP = 12;

function readRecent(): string[] {
    try {
        const raw = localStorage.getItem(RECENT_KEY);
        const list = raw ? (JSON.parse(raw) as unknown) : [];
        return Array.isArray(list) ? list.filter((v): v is string => typeof v === 'string') : [];
    } catch {
        return [];
    }
}

function pushRecent(hex: string): void {
    const next = [hex, ...readRecent().filter((h) => h !== hex)].slice(0, RECENT_CAP);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
}

export interface AciColorPickerProps {
    /** Current value: '#rrggbb', or the BYLAYER/BYBLOCK sentinels. */
    value: string;
    /** Multi-selection with differing colors: show *VARIES* until opened. */
    varies?: boolean;
    onCommit: (value: string) => void;
}

/** One palette swatch cell (data color, hover ring, selected check state). */
function Swatch({ color, selected, label, onPick, wide }: {
    color: string;
    selected: boolean;
    label: string;
    onPick: () => void;
    wide?: boolean;
}) {
    return (
        <Tooltip label={label} placement="bottom" openDelay={350} hasArrow>
            <Box
                as="button"
                role="gridcell"
                aria-label={label}
                onClick={onPick}
                h="16px"
                w={wide ? '32px' : '16px'}
                borderRadius="2px"
                bg={color}
                border="1px solid"
                borderColor={selected ? 'brand.500' : 'border.base'}
                boxShadow={selected ? '0 0 0 2px var(--chakra-colors-brand-500)' : undefined}
                _hover={{ borderColor: 'brand.500' }}
                flexShrink={0}
            />
        </Tooltip>
    );
}

export function AciColorPicker({ value, varies = false, onCommit }: AciColorPickerProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [customHex, setCustomHex] = useState('');
    const recent = useMemo(readRecent, [open]);

    const isSentinel = value === COLOR_BYLAYER || value === COLOR_BYBLOCK;
    const displayColor = varies
        ? undefined
        : isSentinel
            ? '#8ea3b0'
            : value;
    const label = varies
        ? t('ui.cad.varies')
        : value === COLOR_BYLAYER
            ? t('ui.cad.byLayer')
            : value === COLOR_BYBLOCK
                ? t('ui.cad.byBlock')
                : (hexToAci(value) !== null ? `${hexToAci(value)} — ${aciName(hexToAci(value)!)}` : value);

    const pick = (next: string) => {
        if (next.startsWith('#')) pushRecent(next);
        onCommit(next);
        setOpen(false);
    };

    const commitCustom = () => {
        const hex = customHex.trim().toLowerCase();
        if (/^#[0-9a-f]{6}$/.test(hex)) pick(hex);
    };

    return (
        <Popover
            isOpen={open}
            onClose={() => setOpen(false)}
            placement="bottom-start"
            closeOnBlur
            gutter={4}
        >
            <PopoverAnchor>
                <Box
                    as="button"
                    role="button"
                    aria-label={t('ui.cad.pickColor')}
                    onClick={() => setOpen((v) => !v)}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    px={2}
                    py={1}
                    borderRadius="4px"
                    border="1px solid"
                    borderColor="border.base"
                    bg="bg.panel"
                    w="full"
                    minW={0}
                    _hover={{ borderColor: 'border.hover' }}
                >
                    <Box
                        w="14px"
                        h="14px"
                        borderRadius="2px"
                        bg={displayColor ?? 'transparent'}
                        border="1px solid"
                        borderColor="border.hover"
                        flexShrink={0}
                        backgroundImage={displayColor ? undefined : 'repeating-linear-gradient(45deg, var(--chakra-colors-border-base) 0 2px, transparent 2px 4px)'}
                    />
                    <Text fontSize="xs" color="text.primary" noOfLines={1} minW={0}>
                        {label}
                    </Text>
                </Box>
            </PopoverAnchor>
            <PopoverContent w="auto" maxW="min(94vw, 460px)" borderColor="border.base" bg="bg.panel" boxShadow="lg" zIndex={40}>
                <PopoverArrow />
                <PopoverBody p={3}>
                    <Flex direction="column" gap={2}>
                        {/* ByLayer / ByBlock rows */}
                        <Flex gap={2}>
                            <Swatch
                                color="#8ea3b0"
                                wide
                                selected={value === COLOR_BYLAYER}
                                label={t('ui.cad.byLayer')}
                                onPick={() => pick(COLOR_BYLAYER)}
                            />
                            <Swatch
                                color="#b08d5a"
                                wide
                                selected={value === COLOR_BYBLOCK}
                                label={t('ui.cad.byBlock')}
                                onPick={() => pick(COLOR_BYBLOCK)}
                            />
                        </Flex>

                        {/* Full 1-255 ACI grid: 24 columns × 10 tint rows + standard + grays */}
                        <Box overflowX="auto" sx={{
                            scrollbarWidth: 'thin',
                            '&::-webkit-scrollbar': { height: '4px' },
                            '&::-webkit-scrollbar-thumb': { background: 'border.base', borderRadius: '2px' },
                        }}>
                            <Grid templateColumns="repeat(27, 16px)" gap="2px" w="max-content">
                                {ACI_GRID.map((aci) => (
                                    <Swatch
                                        key={aci}
                                        color={aciToHex(aci)}
                                        selected={value.toLowerCase() === aciToHex(aci).toLowerCase()}
                                        label={`${aci} — ${aciName(aci)}`}
                                        onPick={() => pick(aciToHex(aci))}
                                    />
                                ))}
                            </Grid>
                        </Box>

                        {/* Recent strip */}
                        {recent.length > 0 && (
                            <Flex direction="column" gap={1}>
                                <Text fontSize="10px" color="text.secondary" textTransform="uppercase">
                                    {t('ui.cad.recentColors')}
                                </Text>
                                <HStack spacing="2px" flexWrap="nowrap">
                                    {recent.map((hex) => (
                                        <Swatch
                                            key={hex}
                                            color={hex}
                                            selected={value.toLowerCase() === hex.toLowerCase()}
                                            label={hex}
                                            onPick={() => pick(hex)}
                                        />
                                    ))}
                                </HStack>
                            </Flex>
                        )}

                        {/* True color entry (Faz 1 truecolor doors accept hex) */}
                        <Flex gap={2} align="center">
                            <Text fontSize="10px" color="text.secondary" whiteSpace="nowrap" flexShrink={0}>
                                {t('ui.cad.trueColor')}
                            </Text>
                            <Input
                                size="xs"
                                variant="filled"
                                bg="surface.300"
                                color="text.primary"
                                placeholder="#aabbcc"
                                fontFamily="mono"
                                value={customHex}
                                onChange={(e) => setCustomHex(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') commitCustom(); }}
                                minW={0}
                                flex="1"
                            />
                        </Flex>
                    </Flex>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}
